import type {AccountType} from "@hosttools/core/constant";
import {integrations, originalChannels} from "@hosttools/core/constant";
import {
    AddIcon,
    Button,
    ButtonIcon,
    ButtonIconTooltip,
    ButtonSpinner,
    ButtonText,
    HStack,
    ListingChannelsLogo
} from "@hosttools/frontend";
import {type Listing} from "@hosttools/frontend/models/listing";
import {type AccountListing} from "@hosttools/frontend/services/listing";
import {memo, useCallback, useState} from "react";

import useConfirmCloseChannelConnector from "@/client/hooks/useConfirmCloseChannelConnector";
import useConfirmRemoveChannel from "@/client/hooks/useConfirmRemoveChannel";

interface ConnectChannelsProps {
    type: "connectChannels";
    listing: Listing;
    onPress: (listing: Listing) => void;
}

interface MatchListingProps {
    type: "matchListing";
    listing: AccountListing;
    accountID: string;
    onConnect: (accountID: string, externalListingID: string) => Promise<void>;
}

type Props = StrictUnion<ConnectChannelsProps | MatchListingProps>;

const TableActionsColumn: React.FC<Props> = ({listing, type, accountID, onPress, onConnect}) => {
    const [spinner, setSpinner] = useState(false);
    const handleRemoveChannel = useConfirmRemoveChannel(listing);
    const handleCloseChannelConnector = useConfirmCloseChannelConnector(listing);

    const handleConnect = useCallback(async () => {
        try {
            setSpinner(true);
            await onConnect?.(accountID, listing._id);
        } finally {
            setSpinner(false);
        }
    }, [accountID, listing, onConnect]);

    const handlePress = useCallback(() => {
        onPress?.(listing);
    }, [listing, onPress]);

    const handleRemove = useCallback(
        (channel: AccountType | (string & {})) => {
            if (originalChannels.includes(channel) || integrations.includes(channel)) {
                handleRemoveChannel(channel);
                return;
            }

            handleCloseChannelConnector(channel);
        },
        [handleCloseChannelConnector, handleRemoveChannel]
    );

    if (type === "matchListing") {
        return (
            <Button isDisabled={listing.isConnected} variant="outline" onPress={handleConnect}>
                {spinner ? <ButtonSpinner /> : <ButtonIcon as={AddIcon} />}
                <ButtonText>{spinner ? "Connecting..." : "Connect"}</ButtonText>
            </Button>
        );
    }
    return (
        <HStack className="items-center" space="sm">
            <ListingChannelsLogo listing={listing} size="lg" onRemove={handleRemove} />
            <ButtonIconTooltip
                action="primary"
                icon={AddIcon}
                size="sm"
                tooltip="Connect channels"
                onPress={handlePress}
            />
        </HStack>
    );
};

export default memo(TableActionsColumn);
